import {
  CATEGORY_LABELS
} from "./chunk-V7UCD2TT.mjs";

// src/content-generation/template-registry.ts
function createGenerationRegistry(input) {
  const templateMap = new Map(input.templates.map((t) => [t.templateId, t]));
  const candidateMap = new Map(input.candidates.map((c) => [c.candidateId, c]));
  const listTemplates = (surface, format) => input.templates.filter(
    (template) => template.supportedSurfaces.includes(surface) && (!format || template.supportedFormats.includes(format))
  );
  const listCandidates = (surface, format) => input.candidates.filter(
    (candidate) => candidate.supportedSurfaces.includes(surface) && (!format || candidate.supportedFormats.includes(format))
  );
  const getFallbackCandidate = (surface) => {
    const fallbackId = input.fallbackCandidateIds[surface];
    if (fallbackId) {
      const fallback = candidateMap.get(fallbackId);
      if (fallback) {
        return fallback;
      }
    }
    const firstCandidate = listCandidates(surface)[0];
    if (!firstCandidate) {
      throw new Error(`No fallback candidate configured for surface: ${surface}`);
    }
    return firstCandidate;
  };
  return {
    getTemplate: (templateId) => templateMap.get(templateId) ?? null,
    listTemplates,
    getCandidate: (candidateId) => candidateMap.get(candidateId) ?? null,
    listCandidates,
    getFallbackCandidate
  };
}

// src/content-generation/selector.ts
function hasRequiredField(content, field) {
  if (field === "subject") {
    return Boolean(content.subject);
  }
  if (field === "template") {
    return Boolean(content.template);
  }
  return Boolean(content[field]);
}
function scoreCandidate(candidate, input) {
  let score = 0;
  const reasons = [];
  if (candidate.emphasisMode === "subject-first" && input.content.subject) {
    score += 4;
    reasons.push("has-subject");
  }
  if (candidate.emphasisMode === "narrative-first" && !input.content.privacy?.hideSummary) {
    score += 3;
    reasons.push("summary-allowed");
  }
  if (candidate.emphasisMode === "score-first") {
    score += 2;
    reasons.push("score-available");
  }
  if (candidate.emphasisMode === "category-first") {
    score += 1;
    reasons.push("category-available");
  }
  if (input.content.variantHint === "rewrite-journey") {
    score += 2;
    reasons.push("variant-hint-rewrite-journey");
  }
  if (candidate.family.includes("rewrite-journey") && input.content.variantHint !== "rewrite-journey") {
    score -= 10;
    reasons.push("rewrite-journey-not-requested");
  }
  if (candidate.artDirection === "alert-high-contrast" && (input.content.score >= 8 || input.content.category === "danger")) {
    score += 5;
    reasons.push("high-severity-alert-fit");
  }
  if (candidate.artDirection === "clean-analytic" && input.content.score <= 4 && input.content.variantHint !== "rewrite-journey") {
    score += 4;
    reasons.push("low-drama-clean-fit");
  }
  if (candidate.artDirection === "soft-guidance" && input.content.template?.voice && ["supportive", "warm", "coach", "gentle"].includes(input.content.template.voice)) {
    score += 5;
    reasons.push("supportive-voice-soft-guidance-fit");
  }
  if (candidate.cachePolicyHint === "public-deterministic" && input.surface.deliveryMode === "public-cacheable") {
    score += 2;
    reasons.push("public-cache-aligned");
  }
  if (candidate.cachePolicyHint === "private-ephemeral" && input.surface.deliveryMode === "private-ephemeral") {
    score += 2;
    reasons.push("private-ephemeral-aligned");
  }
  if (input.content.privacy?.hideSummary && candidate.emphasisMode === "narrative-first") {
    score -= 4;
    reasons.push("summary-hidden-penalty");
  }
  if (input.surface.deliveryMode === "private-ephemeral" && candidate.artDirection === "editorial-dark") {
    score -= 2;
    reasons.push("private-surface-editorial-dark-penalty");
  }
  return { score, reasons };
}
function applyOverride(candidates, override) {
  if (!override) {
    return candidates;
  }
  let filtered = candidates;
  if (override.blockCandidateIds?.length) {
    filtered = filtered.filter(
      (candidate) => !override.blockCandidateIds?.includes(candidate.candidateId)
    );
  }
  if (override.forceCandidateId) {
    filtered = filtered.filter((candidate) => candidate.candidateId === override.forceCandidateId);
  }
  if (override.forceArtDirection) {
    filtered = filtered.filter((candidate) => candidate.artDirection === override.forceArtDirection);
  }
  if (override.forceTemplateId) {
    filtered = filtered.filter((candidate) => candidate.templateId === override.forceTemplateId);
  }
  return filtered;
}
function resolveFallbackCandidate(input) {
  const configuredFallbackId = input.policy.fallback.defaultCandidateId;
  const configuredFallback = input.candidates.find(
    (candidate) => candidate.candidateId === configuredFallbackId
  );
  return configuredFallback ?? input.candidates[0] ?? null;
}
function selectGenerationCandidate(input) {
  let candidates = input.candidates.filter(
    (candidate) => candidate.supportedSurfaces.includes(input.surface.surface) && candidate.supportedFormats.includes(input.surface.format)
  );
  candidates = applyOverride(candidates, input.override);
  if (input.policy.exclusions?.bannedCandidateIds?.length) {
    candidates = candidates.filter(
      (candidate) => !input.policy.exclusions?.bannedCandidateIds?.includes(candidate.candidateId)
    );
  }
  if (input.policy.exclusions?.bannedArtDirections?.length) {
    candidates = candidates.filter(
      (candidate) => !input.policy.exclusions?.bannedArtDirections?.includes(candidate.artDirection)
    );
  }
  candidates = candidates.filter((candidate) => candidate.requiredFields.every((field) => hasRequiredField(input.content, field)));
  const scored = candidates.map((candidate) => ({
    candidate,
    ...scoreCandidate(candidate, input)
  })).sort((a, b) => b.score - a.score);
  const fallbackCandidate = resolveFallbackCandidate(input);
  const selected = scored[0] ?? (fallbackCandidate ? {
    candidate: fallbackCandidate,
    score: 0,
    reasons: fallbackCandidate.candidateId === input.policy.fallback.defaultCandidateId ? ["fallback-policy-default-candidate"] : ["fallback-first-candidate"]
  } : null);
  if (!selected) {
    throw new Error(`No candidates available for surface ${input.surface.surface}`);
  }
  return {
    selectedCandidateId: selected.candidate.candidateId,
    selectedTemplateId: selected.candidate.templateId,
    emphasisMode: selected.candidate.emphasisMode,
    artDirection: selected.candidate.artDirection,
    explanationCodes: selected.reasons.length > 0 ? selected.reasons : ["fallback-first-candidate"],
    fallbackApplied: scored.length === 0
  };
}

// src/content-generation/resolver.ts
function safeSummary(input) {
  if (input.content.privacy?.hideSummary) {
    return "Analysis available";
  }
  if (input.content.template?.shareText) {
    return input.content.template.shareText;
  }
  if (input.content.subject?.body) {
    return input.content.subject.body;
  }
  return input.content.summary;
}
function badgeLabel(category) {
  return CATEGORY_LABELS[category] ?? category.toUpperCase();
}
function pushBlock(blocks, block) {
  if (block && block.value) {
    blocks.push(block);
  }
}
function resolveGenerationContent(input) {
  const summaryText = safeSummary(input);
  const headlineText = input.selection.emphasisMode === "subject-first" && input.content.subject?.title ? input.content.subject.title : input.content.episodeTitle ?? "DramaDetector";
  const blocks = [];
  pushBlock(blocks, { kind: "headline", value: headlineText, visibility: "always" });
  pushBlock(blocks, {
    kind: "summary",
    value: summaryText,
    visibility: input.content.privacy?.hideSummary ? "if-allowed" : "always"
  });
  pushBlock(blocks, {
    kind: "badge",
    value: badgeLabel(input.content.category),
    visibility: "always"
  });
  pushBlock(blocks, {
    kind: "metric",
    value: `${input.content.score}`,
    visibility: "always"
  });
  if (input.content.episodeTitle) {
    pushBlock(blocks, {
      kind: "context",
      value: input.content.episodeTitle,
      visibility: "if-space"
    });
  }
  if (input.surface.surface === "og-image") {
    pushBlock(blocks, {
      kind: "cta",
      value: "View Analysis",
      visibility: "if-space"
    });
  }
  return {
    blocks,
    variables: {
      scoreLabel: `${input.content.score}`,
      scoreValue: input.content.score,
      categoryLabel: badgeLabel(input.content.category),
      categoryKey: input.content.category,
      summaryText,
      episodeTitle: input.content.episodeTitle ?? null,
      badgeText: "Drama Score",
      ctaText: input.surface.surface === "og-image" ? "View Analysis" : null,
      hasSubject: Boolean(input.content.subject),
      hasTemplateVoice: Boolean(input.content.template?.voice),
      patternsCount: input.content.privacy?.hidePatterns ? null : input.content.patternsCount ?? null
    },
    tokens: {
      artDirection: input.selection.artDirection,
      emphasisMode: input.selection.emphasisMode,
      deliveryMode: input.surface.deliveryMode
    }
  };
}

// src/content-generation/output.ts
import { randomUUID } from "crypto";
function buildGenerationCoreOutput({
  input,
  policy,
  registry
}) {
  const candidates = registry.listCandidates(input.surface.surface, input.surface.format);
  const selection = selectGenerationCandidate({
    content: input.content,
    surface: input.surface,
    policy,
    candidates
  });
  const candidate = registry.getCandidate(selection.selectedCandidateId);
  const template = registry.getTemplate(selection.selectedTemplateId);
  if (!candidate || !template) {
    throw new Error("Selected candidate or template missing from registry");
  }
  const resolved = resolveGenerationContent({
    content: input.content,
    surface: input.surface,
    selection,
    policy
  });
  return {
    decisionId: randomUUID(),
    selectedCandidateId: candidate.candidateId,
    selectedTemplateId: template.templateId,
    family: candidate.family,
    version: candidate.version,
    surface: {
      surface: input.surface.surface,
      format: input.surface.format,
      deliveryMode: input.surface.deliveryMode,
      renderingMode: template.renderingMode
    },
    selection: {
      emphasisMode: selection.emphasisMode,
      artDirection: selection.artDirection,
      explanationCodes: selection.explanationCodes,
      fallbackApplied: selection.fallbackApplied
    },
    resolved,
    artifactHints: {
      cachePolicyHint: candidate.cachePolicyHint,
      candidateVersion: candidate.version,
      artifactVersion: policy.version
    }
  };
}

export {
  createGenerationRegistry,
  selectGenerationCandidate,
  resolveGenerationContent,
  buildGenerationCoreOutput
};
